import React from 'react';

const { userProfileSectionTitles } = require('buyside/util/constantsFromRails.json');

import Link from 'buyside/components/presentation/Link/link';
import SubmitResearchButton from 'buyside/components/presentation/Button/SubmitResearchButton/submitResearchButton';

import AcademicPerformance from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/AcademicPerformance/academicPerformance';
import AccreditedInvestorProfile from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/AccreditedInvestorProfile/userAccreditedInvestorProfile';
import Awards from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/Awards/awards';
import Certifications from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/Certifications/certifications';
import Education from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/Education/education';
import Expertise from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/Expertise/expertise';
import PersonalDetails from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/PersonalDetails/personalDetails';
import QAndA from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/QAndA/qAndA';
import Research from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/Research/research';
import ResearchPerformance from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/ResearchPerformance/researchPerformance';
import ResumePhone from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/ResumePhone/resumePhone';
import WorkExperience from 'buyside/components/presentation/UserProfileShowPage/UserProfileSection/WorkExperience/workExperience';

import AcademicPerformanceForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/AcademicPerformanceForm/academicPerformanceForm';
import AccreditedInvestorProfileForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/AccreditedInvestorProfile/userAccreditedInvestorProfileForm';
import CertificationsForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/CertificationsForm/certificationsForm';
import EducationForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/EducationForm/educationForm';
import ExpertiseForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/ExpertiseForm/expertiseForm';
import PersonalDetailsForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/PersonalDetailsForm/personalDetailsForm';
import QAndAForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/QAndAForm/qAndAForm';
import ResearchForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/ResearchForm/researchForm';
import ResumePhoneForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/ResumePhoneForm/resumePhoneForm';
import WorkExperienceForm from 'buyside/components/presentation/UserProfileShowPage/UserProfileForm/WorkExperienceForm/workExperienceForm';

import {
  workExperienceSchema,
  educationSchema,
  personalDetailsSchema,
  resumePhoneSchema,
  academicPerformanceSchema,
  researchSchema
} from 'buyside/util/userProfileShowPageValidationSchemata';
import { userAccreditedInvestorQuestionnaireUrl } from 'buyside/util/urlHelper';

export const SECTIONS = Object.keys(userProfileSectionTitles);

export const ICONS = {};

export const TITLES = userProfileSectionTitles;

export const MINIMUM_ATTRIBUTES = {
  academic_performance: ['profileTestResults'],
  awards: ['user.userBadges.edges', 'user.historicalPublicRankings'],
  certifications: ['certifications'],
  education: ['schoolHistories'],
  expertise: ['sectorExpertise', 'geographicExpertise', 'assetClassExpertise'],
  personal_details: [
    'hometown',
    'investmentHero',
    'favoriteInvestmentBook',
    'investingStyle',
    'languagesSpoken'
  ],
  q_and_a: ['hasAnyProfileAnswers'],
  research_performance: ['id'],
  research: ['user.recentIdea', 'profileAttachments'],
  resume_phone: ['resume', 'workPhone'],
  tags: [],
  work_experience: ['employerHistories']
};

export const EMPTY_TEXT = {
  awards: 'Section Empty. Earn a ranking or badge to add to this section.',
  accredited_investor_profile: (
    <div>
      <p>Are you an accredited investor looking to invest in hedge funds?</p>
      <div>
        <Link to={userAccreditedInvestorQuestionnaireUrl()}>Complete a survey</Link> to start
        engaging with fund managers directly.
      </div>
    </div>
  ),
  expertise:
    'Section Empty. Click the "Edit" button to add your sector, geographic, and asset class expertise.',
  education: 'Section Empty. Click the "Edit" button to add your educational background.',
  personal_details:
    'Section Empty. Click the "Edit" button to add personal details and let others know what defines you as an investor.',
  certifications:
    'Section Empty. Click the "Edit" button to add any certifications that you have earned. These will also display under your name in your profile header.',
  resume_phone:
    'Section Empty. Click the "Edit" button to add your resume and phone number. These will only be visible to you and will be provided as default options when applying to jobs.',
  academic_performance:
    'Section Empty. Click the "Edit" button to add your GPA and standardized test scores. These scores will only be visible to you and hiring managers after applying to jobs.',
  research: (
    <div>
      <p>
        Section Empty. Publish research on SumZero to display your most recent Idea, and/or click
        the "Edit" button to add a PDF of a research idea. This section will only be visible to you
        and hiring managers after applying to jobs.
      </p>
      <div>
        <SubmitResearchButton />
      </div>
    </div>
  ),
  q_and_a:
    'Section Empty. Click the "Edit" button to answer some interview-style questions. Your answers will only be visible to you and hiring managers after applying for jobs.',
  default: 'Section Empty. Click the "Edit" button to add information.'
};

export const COMMUNITY = 'general_community_member';
export const HIRING_MANAGER = 'hiring_manager';
export const ADMIN = 'admin';

export const VISIBILITIES = {
  resume_phone: ADMIN,
  academic_performance: HIRING_MANAGER,
  q_and_a: HIRING_MANAGER
};

export const COMPONENTS = {
  academic_performance: AcademicPerformance,
  accredited_investor_profile: AccreditedInvestorProfile,
  awards: Awards,
  certifications: Certifications,
  education: Education,
  expertise: Expertise,
  personal_details: PersonalDetails,
  q_and_a: QAndA,
  research_performance: ResearchPerformance,
  research: Research,
  resume_phone: ResumePhone,
  work_experience: WorkExperience
};

export const EDIT_FORMS = {
  academic_performance: AcademicPerformanceForm,
  accredited_investor_profile: AccreditedInvestorProfileForm,
  certifications: CertificationsForm,
  education: EducationForm,
  expertise: ExpertiseForm,
  personal_details: PersonalDetailsForm,
  q_and_a: QAndAForm,
  research: ResearchForm,
  resume_phone: ResumePhoneForm,
  work_experience: WorkExperienceForm
};

export const EDIT_FORM_BOX_TEXT = {
  resume_phone:
    'Upload your resume as a PDF (if it is a Word document, export it as a PDF before uploading).',
  academic_performance: 'Do not add multiple results for the same test or for GPA.'
};

export const VALIDATION_SCHEMATA = {
  work_experience: workExperienceSchema,
  education: educationSchema,
  personal_details: personalDetailsSchema,
  resume_phone: resumePhoneSchema,
  academic_performance: academicPerformanceSchema,
  research: researchSchema
};
